import React, { FC, useEffect, useMemo, useState } from 'react';
import styles from './index.module.scss';
import { Form, Spin } from 'antd';
import { useRequest } from 'ahooks';
import { Button, Empty, Paging, Select, Tag, useSearchParams, useUpdate } from '@maxtropy/components';
import {
  apiV2LightmapSunlightDeviceListPost,
  apiV2LightmapSunlightDevicePagePost,
} from '@maxtropy/device-customer-apis-v2';
import { RTU_STATUS, RTU_STATUS_MAP } from './util';
import { AreaItem } from '../MapView';
import { keepTwoOrLine } from '@/utils/utils';

export interface ISunLightPanel {
  record?: AreaItem;
  lightVisible: boolean;
}

const SunLightPanel: FC<ISunLightPanel> = props => {
  const { record, lightVisible } = props;
  const [deviceId, setDeviceId] = useState<number>();
  const [update, updateFn] = useUpdate();
  const pagingInfo = useSearchParams(50);

  const { pageOffset, pageSize, setTotalCount } = pagingInfo;

  const { data: deviceList } = useRequest(
    async () => {
      const res = await apiV2LightmapSunlightDeviceListPost({ id: record?.id });
      return res.list;
    },
    {
      ready: !!record?.id && lightVisible,
      refreshDeps: [record?.id, lightVisible],
    }
  );

  const { data, loading } = useRequest(
    () =>
      apiV2LightmapSunlightDevicePagePost({
        deviceIds: deviceId ? [deviceId!] : undefined,
        id: record?.id,
        page: pageOffset,
        size: pageSize,
      }).then(res => {
        setTotalCount(res.total ?? 0);
        return res.list;
      }),
    {
      ready: lightVisible && !!record?.id,
      refreshDeps: [deviceId, pageOffset, pageSize, update, lightVisible, record?.id],
    }
  );

  const deviceOptions = useMemo(() => {
    return (deviceList ?? []).map(item => ({
      label: item.deviceName,
      value: item.deviceId,
    }));
  }, [deviceList]);

  return (
    <div className={styles.panel}>
      <div className={styles.titleContent}>
        <div className={styles.title}>{record?.zoneName ?? '--'}</div>
        {/* <CloseOutlined style={{ fontSize: '20px' }} /> */}
      </div>
      <div className={styles.content}>
        <Form.Item label="控制设备名称" style={{ margin: '0 0 16px' }}>
          <Select
            placeholder="请选择"
            optionFilterProp="label"
            showSearch
            allowClear
            options={deviceOptions}
            onChange={(value: number) => {
              setDeviceId(value);
            }}
            value={deviceId}
            style={{ width: 280 }}
          />
        </Form.Item>
        <Spin spinning={loading}>
          {data && data.length > 0 ? (
            <>
              <div className={styles.card}>
                {data?.map(item => (
                  <div key={item.deviceId} className={styles.cardItem}>
                    <div className={styles.cardTitleContent}>
                      <span className={styles.cardTitle}>{item.deviceName}</span>
                      <Button
                        type="link"
                        style={{ padding: 0 }}
                        onClick={() => {
                          window.open(`/data/history/device?deviceId=${item.deviceId}`, '_blank');
                        }}
                      >
                        更多{'>>'}
                      </Button>
                    </div>
                    <div className={styles.cardInfo}>
                      <div className={styles.twoCol}>
                        <div>
                          温度：<b>{keepTwoOrLine(item.temperature)}℃</b>
                        </div>
                        <div>
                          电池电量：<b>{keepTwoOrLine(item.batteryCapacity)}kwh</b>
                        </div>
                      </div>
                      <div className={styles.twoCol}>
                        <div>
                          太阳板电压：<b>{keepTwoOrLine(item.solarPanelVoltage)}V</b>
                        </div>
                        <div>
                          蓄电池电压：<b>{keepTwoOrLine(item.electricityVoltage)}V</b>
                        </div>
                      </div>
                      <div>
                        放电电压：
                        <b style={{ color: 'var(--mx-diagram1-color)' }}>{keepTwoOrLine(item.dischargeVoltage)}V</b>
                      </div>
                      <div className={styles.twoCol}>
                        <div>
                          充电电流：
                          <b style={{ color: 'var(--mx-diagram5-color)' }}>{keepTwoOrLine(item.chargingCurrent)}A</b>
                        </div>
                        <div>
                          放电电流：
                          <b style={{ color: 'var(--mx-diagram1-color)' }}>{keepTwoOrLine(item.dischargeCurrent)}A</b>
                        </div>
                      </div>
                      <div className={styles.twoCol}>
                        <div>
                          总充电安时数：
                          <b style={{ color: 'var(--mx-diagram5-color)' }}>
                            {keepTwoOrLine(item.totalChargingAmpereHours)}h
                          </b>
                        </div>
                        <div>
                          总放电安时数：
                          <b style={{ color: 'var(--mx-diagram1-color)' }}>
                            {keepTwoOrLine(item.totalDischargingAmpereHours)}h
                          </b>
                        </div>
                      </div>
                      <div>
                        rtu状态：
                        {item.rtuStatus ? (
                          <Tag
                            border="solid"
                            color={
                              [RTU_STATUS.idle].includes(item.rtuStatus)
                                ? 'default'
                                : [
                                    RTU_STATUS.charging_1,
                                    RTU_STATUS.charging_2,
                                    RTU_STATUS.charging_3,
                                    RTU_STATUS.charging_4,
                                    RTU_STATUS.charging_5,
                                  ].includes(item.rtuStatus)
                                ? 'success'
                                : [RTU_STATUS.discharge_1, RTU_STATUS.discharge_2, RTU_STATUS.discharge_end].includes(
                                    item.rtuStatus
                                  )
                                ? 'info'
                                : [RTU_STATUS.manual_light].includes(item.rtuStatus)
                                ? 'info'
                                : 'error'
                            }
                          >
                            {RTU_STATUS_MAP[item.rtuStatus as RTU_STATUS]}
                          </Tag>
                        ) : (
                          '-'
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <Paging pagingInfo={pagingInfo} className={styles.paging} />
            </>
          ) : (
            <div className={styles.empty}>
              <Empty />
            </div>
          )}
        </Spin>
      </div>
    </div>
  );
};
export default SunLightPanel;
